import { t } from "@lingui/macro";
import {
  type BrandReportOptionsQuery,
  BrandRevenueGroupByEnum,
  BrandRevenueOrderByColumnEnum,
  type BrandRevenueQueryArgs,
  BrandRevenueWhereColumn,
  type ExpenseBudgetCategory,
  SortOrder,
  SqlOperator,
} from "@src/__generated__/urql-graphql";
import type { CategoryName } from "@src/components/modules/reports/project/common/types";
import type { CalendarAction } from "@src/components/ui-kit/datePicker/CalendarActions";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import map from "@src/utils/map-to-options";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import type { DateRange } from "@src/utils/types";
import { getParamFromQuery } from "@src/utils/url";
import {
  endOfMonth,
  endOfYear,
  startOfMonth,
  startOfYear,
  subMonths,
  subYears,
} from "date-fns";
import { action, computed, makeObservable, observable } from "mobx";
import { now } from "mobx-utils";
import type { Query } from "nextjs-routes";

export class BrandReportStore {
  pagination: PaginationState;
  where: Filters<BrandRevenueWhereColumn>;
  @observable searchTerm = "";
  @observable dateRange: DateRange = {
    start: startOfMonth(now()),
    end: endOfMonth(now()),
  };

  @observable groupBy = BrandRevenueGroupByEnum.None;
  @observable selectedDetailGrossIncome = 0;
  @observable uncolapsedRows: Set<CategoryName> = new Set();
  @observable expenseBudgetCategories: NonNullable<
    Pick<ExpenseBudgetCategory, "id" | "name">
  >[] = [];

  constructor(key: string) {
    makeObservable(this);
    this.pagination = new PaginationState(key);
    this.where = new Filters<BrandRevenueWhereColumn>(
      [
        new Filter({
          column: BrandRevenueWhereColumn.BrandId,
          operator: SqlOperator.In,
          title: t`Brand`,
          options: [],
          sectioned: true,
          hasSelectAllOption: true,
        }),
      ],
      {
        onChange: this.pagination.resetPage,
        tableKey: key,
        orderBy: this.orderBy,
      },
    );
  }

  orderBy = new OrderBy<BrandRevenueOrderByColumnEnum>([
    {
      column: BrandRevenueOrderByColumnEnum.BrandName,
      order: SortOrder.Asc,
    },
  ]);

  @action.bound initFilters(query: Query | Partial<Query>) {
    this.searchTerm = getParamFromQuery("search", query) ?? "";
    this.where.fromURLSearchParam(query);
    this.orderBy.fromURLSearchParam(query);
    this.pagination.fromURLSearchParam(query);

    const from = getParamFromQuery("from", query);
    const to = getParamFromQuery("to", query);

    if (from && to) {
      this.setDateRange({
        start: new Date(from),
        end: new Date(to),
      });
    }
  }

  @action.bound setDateRange(newRange: DateRange) {
    this.dateRange = newRange;
  }

  @action setGroupBy(groupBy: BrandRevenueGroupByEnum) {
    this.groupBy = groupBy;
  }

  @computed get filters(): BrandRevenueQueryArgs {
    return {
      where: this.queryVariables.filters.where,
      search: this.queryVariables.filters.search,
      orderBy: this.queryVariables.filters.orderBy,
      from: this.dateRange.start,
      to: this.dateRange.end,
      page: this.queryVariables.page,
      first: this.queryVariables.first,
      groupBy: this.groupBy,
    };
  }

  @computed get queryVariables() {
    return commonQueryVariables(this);
  }

  @computed get uriParams() {
    return { ...commonSearchParams(this), from: Date, to: Date };
  }

  @computed get periodPickerActions(): Array<CalendarAction> {
    const today = new Date(now());
    return [
      {
        title: t`This month`,
        getDates: () => {
          return {
            start: startOfMonth(today),
            end: endOfMonth(today),
          };
        },
      },
      {
        title: t`Last month`,
        getDates: () => {
          return {
            start: startOfMonth(subMonths(today, 1)),
            end: endOfMonth(subMonths(today, 1)),
          };
        },
      },
      {
        title: t`This year`,
        getDates: () => {
          return {
            start: startOfYear(today),
            end: endOfYear(today),
          };
        },
      },
      {
        title: t`Last year`,
        getDates: () => {
          return {
            start: startOfYear(subYears(today, 1)),
            end: endOfYear(subYears(today, 1)),
          };
        },
      },
    ];
  }

  @action setTableOptions(query: BrandReportOptionsQuery) {
    this.where.filtersByColumn
      .get(BrandRevenueWhereColumn.BrandId)
      ?.setOptions(map.clients.toOptions(query.clientsSimpleMap));

    this.where.filtersByColumn
      .get(BrandRevenueWhereColumn.ProjectId)
      ?.setOptions(map.projects(query.projectsSimpleMap));
  }

  @action setExpenseBudgetCategories(
    expenseBudgetCategories: Pick<ExpenseBudgetCategory, "id" | "name">[],
  ) {
    this.expenseBudgetCategories = expenseBudgetCategories;
  }
}
